import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';

export type LabeledValuePayload = {
	readonly value: string;
	readonly label: string;
};

// Shape shared by every call setting, flat on the wire (bounds sit alongside `own`/`effective`, not
// nested). `own === null` means no override at this level; `effective === null` means nobody decided at
// any level — distinct from an explicit empty override (`[]` for the vocabulary, `''` for a prompt).
// `effectiveLevel` is `null` if and only if `effective` is `null`. `supported` is `true` when no model
// resolves for the usage (no model to reject the setting). `unsupportedReason` is a ready-to-display
// French sentence, never a translation key.
export type UsageSettingPayload<TValue> = {
	readonly own: TValue | null;
	readonly effective: TValue | null;
	readonly effectiveLevel: LabeledValuePayload | null;
	readonly applicable: boolean;
	readonly editable: boolean;
	readonly supported: boolean;
	readonly unsupportedReason: string | null;
};

// `own`/`effective` are a bare array of strings, never `{ value, label }[]` — same asymmetry already
// assumed by `AiModel.usages`. `maxTerms`/`maxTermLength` are the bounds the server actually applies,
// never `null`.
export type VocabularyTermsPayload = UsageSettingPayload<readonly string[]> & {
	readonly maxTerms: number;
	readonly maxTermLength: number;
};

// No length limit: the server accepts a prompt of any size.
export type PromptPayload = UsageSettingPayload<string>;

// Account-level setting: `own`/`effective` are a bare number, never a string — the column is
// `double precision`, so `0.1` comes back as `0.1`. Applicable only to a non-audio usage.
export type TemperaturePayload = UsageSettingPayload<number> & {
	readonly minimum: number;
	readonly maximum: number;
};

// Always the four keys, at every level and for every usage, even when a setting is not applicable to
// this usage — the response shape never depends on usage or level.
export type UsageSettingsPayload = {
	readonly vocabularyTerms: VocabularyTermsPayload;
	readonly contextPrompt: PromptPayload;
	readonly systemPrompt: PromptPayload;
	readonly temperature: TemperaturePayload;
};

// A hand-built object, not a serialized entity: identifiers travel as bare numbers, never as `{ id }`
// relations. `hasOwnAssignment` says whether this level has its own row; `ownModelId === null` alone
// does not, since a live usage can be switched off at this level without a model.
// `effectiveModelId === null` means no model at any level — there is no fallback to assume.
export type ModelAssignmentPayload = {
	readonly usage: LabeledValuePayload;
	readonly hasOwnAssignment: boolean;
	readonly ownModelId: number | null;
	readonly effectiveModelId: number | null;
	readonly effectiveLevel: LabeledValuePayload | null;
	readonly settings: UsageSettingsPayload;
};

// Always one entry per `AiUsage` value, in enum declaration order — including unassigned usages.
export type ModelAssignmentsFetchedPayload = ModelAssignmentPayload[];

export type ModelAssignmentsFetchedAction = {
	type: 'MODEL_ASSIGNMENTS_FETCHED';
	payload: ModelAssignmentsFetchedPayload;
	scope: AssignmentScope;
};

export default (
	payload: ModelAssignmentsFetchedPayload,
	scope: AssignmentScope,
): ModelAssignmentsFetchedAction => ({
	type: 'MODEL_ASSIGNMENTS_FETCHED',
	payload,
	scope,
});
